<template>
  <div>
    <div v-for="(param, paramIdx) in props.func[props.parameterKey]"
      class=" py-1"
      :key="props.parameterKey +'-parameter-'+paramIdx">

        <div v-if="param.name !== undefined" class="text-xs text-gray-500 mb-0.5">{{ param.name }}</div>
        <div class="flex items-center">
        <!-- otherwise, regular input -->
        <div class="relative" style="min-width: 160px;">
          <div
              v-show="props.parameterKey !== 'target' && (props.func['showTypeOptions'] === undefined || props.func['showTypeOptions'][props.parameterKey] === true)"
              class=" absolute left-0 text-black pr-1 flex items-center h-full"
              style="transform: translateX(-100%);">
              <b-form-select
                v-model="param.type"
                :options="sl ? ['Val', 'SL', 'Col'] : ['Val', 'Col']"
                class=" pl-0"
                @change="() => updateParamType(paramIdx, param.type)">
              </b-form-select>              
          </div>

          <input
            v-if="param.type === 'Val'"
            class="h-10 w-full rounded-md border-none focus:outline focus:outline-blue-300"
            ref="paramInput"
            :value="param.value"
            :title="param.name === undefined ? 'Valeur' : param.name"
            :placeholder="param.name === undefined ? 'Valeur' : ''"
            @keydown="(evt) => { if ((evt.key === 'Delete' || evt.key === 'Backspace') && evt.target.value.length === 0) { onRemoveParameter(paramIdx, param) } }"
            @input="(evt) => onChange(evt.target.value, paramIdx)"
            autofocus
          >
          <autocomplete
          v-if="param.type === 'Col'"
            base-class="column-autocomplete"

            :defaultValue="param.value"

            @submit="(val) => onChange(val, paramIdx)"
            @keydown="(evt) => { if ((evt.key === 'Delete' || evt.key === 'Backspace') && evt.target.value.length === 0) { onRemoveParameter(paramIdx, param) } }"

            @change="
                (evt) => onChange(evt.target.value, paramIdx)
            "
            :search="searchColumns"
            :autoSelect="true"
            :title="param.name === undefined ? 'Colonne' : param.name"
            :placeholder="param.name === undefined ? 'Colonne' : ''"
            :submitOnEnter="true"
          >
          </autocomplete>

          <div v-if='param.type === "SL"'>Saut de ligne</div>
        </div>

        <button
          class="ml-1 text-red-400 hover:text-red-600 border-0 bg-transparent focus:outline-none"
          title="Supprimer"
          @click.prevent="() => onRemoveParameter(paramIdx, param)">
          <i class="fas fa-times"></i>
        </button>
        </div>
    </div>

    <!-- IF we are under max-length -->
    <div class=" w-full flex justify-center pt-1">
     
      <b-dropdown :variant="variantFromParameterKey" toggle-class="border-0" :disabled="!(maxLength === undefined || func[parameterKey].length < maxLength)">
        <template #button-content>
          <i :class="iconFromParameterKey"></i>
        </template>

        <div class=" overflow-y-scroll max-h-60">
          <div v-if="expectedArguments">
            <b-dropdown-item-button
              v-for="(arg, argIdx) in expectedArgumentsRef"
              :key="'dropdown-arg-'+argIdx"
              @click="() => addParameter('', arg)">
              {{ arg.name }}
            </b-dropdown-item-button>
          </div>
              
          <div v-else>
            <b-dropdown-item-button
              @click="() => addParameter('')">
              {{ props.parameterKey === "target" ? "Nouvelle colonne" : "Champ libre" }}
            </b-dropdown-item-button>
            <b-dropdown-item-button v-if="sl"
              @click="() => addParameter('', null, 'SL')">
              Saut de ligne
            </b-dropdown-item-button>
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item-button
              v-for="(column, dropdownColIdx) in props.availableColumns"
                :key="'dropdown-column-'+dropdownColIdx"
                @click="() => addParameter(column, null, 'Col')">
                {{column}}
            </b-dropdown-item-button>
          </div>
        </div>

      </b-dropdown>
    </div>
  </div>
</template>

<script setup>
import Autocomplete from "@trevoreyre/autocomplete-vue";
import { defineProps, defineEmits, ref, computed, watch, nextTick, onMounted } from 'vue';

// emits: change, delete, add
const emits = defineEmits(['change', 'delete', 'add']);

const paramInput = ref(null);

const props = defineProps({
  func: {
    type: Object,
    required: true
  },
  parameterKey: {
    type: String,
    required: true
  },
  availableColumns: {
    type: Array,
    required: true
  },
  maxLength: {
    type: Number,
    default: undefined
  },
  expectedArguments: {
    type: Array,
    default: undefined
  },
  sl: {}
});

const updateParamType = (paramIdx, type) => {
  console.log("Updating param type: " + paramIdx + " - " + type);
  let newVal = props.func[props.parameterKey][paramIdx];
  newVal.type = type;
  emits('change', newVal, paramIdx);
}

const expectedArgumentsRef = ref(props.expectedArguments);

const iconFromParameterKey = computed(() => {
  switch (props.parameterKey) {
    case "target":
      return "fas fa-file-import";
    case "origin":
      return "fas fa-plus";
    default:
      return "fas fa-font";
  }
});

const variantFromParameterKey = computed(() => {
  switch (props.parameterKey) {
    case "target":
      return "warning";
    case "origin":
      return "primary";
    default:
      return "secondary";
  }
});

const onChange = (newValue, paramIdx) => {
  // console.log("New value for index: ", paramIdx, newValue);
  let param = props.func[props.parameterKey][paramIdx];
  param.value = newValue;
  emits('change', param, paramIdx);
}

const onRemoveParameter = (paramIdx, param = undefined) => {
  // if the parameter had a name, re-add it to the expectedArguments
  if (param && props.expectedArguments !== undefined) {
    const param = props.func[props.parameterKey][paramIdx];

    let paramInExpectedArgs = expectedArgumentsRef.value.find((argument) => argument.name === param.name);

    if (param.name && paramInExpectedArgs === undefined) {
      // Re-add the FULL original definition (argumentKey, initialValue, autoFill...)
      // from the original prop, not a stripped { name, single } object — otherwise
      // re-adding this argument later via the dropdown loses its argumentKey and
      // required-argument validation never matches it again.
      let originalArg = props.expectedArguments.find((argument) => argument.name === param.name);
      expectedArgumentsRef.value.push(originalArg ? { ...originalArg } : { name: param.name, single: true });
    }
  }
  emits('delete', paramIdx);
}

const addParameter = (value, arg = null, defaultType = 'Val') => {

  if (props.parameterKey === 'target') {
    defaultType = 'Col';
  }

  if (arg && arg.single === true) {
    expectedArgumentsRef.value = expectedArgumentsRef.value.filter((argument) => argument.name !== arg.name);

    // Valeur par défaut : autoFill si défini, sinon la valeur passée en argument
    // (ex: initialValue lors de l'ajout automatique au montage), sinon vide.
    let initial = value !== undefined && value !== null ? value : '';
    if (arg.autoFill !== undefined) {
      initial = arg.autoFill;
    }

    value = { value: initial, name: arg.name, argumentKey: arg.argumentKey, type: defaultType};
  }

  // console.log("Sending out default type: ", defaultType);

  emits('add', value, defaultType);

  setTimeout(() => {
      document.activeElement.blur();
      if (paramInput.value === null) {
        return;
      }
      let lastParamInputIdx = paramInput.value.length;
      if (paramInput.value[lastParamInputIdx - 1] === undefined) {
        return;
      }
      paramInput.value[lastParamInputIdx - 1].focus();
    }, 100);
}

onMounted(() => {

  // if we already have max arguments, return
  if (props.maxLength !== undefined && props.func[props.parameterKey].length >= props.maxLength) {
    return;
  }

  if (props.expectedArguments !== undefined) {
    for (const expectedArg of props.expectedArguments) {
      if (expectedArg.initialValue !== undefined) {
        addParameter(expectedArg.initialValue, expectedArg);
      }
    }
  }
});

const searchColumns = ref((input) => {
  if (!input || input.length < 1) { return props.availableColumns || [] }

  return props.availableColumns.filter((column) => {
    return column.toLowerCase().includes(input.toLowerCase());
  });

});

</script>

<style>
.column-autocomplete-input {
  height: 2.5rem;
  width: 100%;
  border-radius: 0.375rem;
  border: none;
  outline: none;
  background-color: white;
  padding: 0 0.5rem;
  box-sizing: border-box;
}

.column-autocomplete-input:focus {
  outline-style: solid;
  outline-width: 1px;
  outline-color: #93c5fd;
}



.column-autocomplete-result-list {
  margin: 0;
  border: 1px solid rgba(0, 0, 0, 0.12);
  padding: 0;
  box-sizing: border-box;
  max-height: 296px;
  overflow-y: auto;
  background: #fff;
  list-style: none;
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.16);
}

[data-position="below"] .column-autocomplete-result-list {
  margin-top: -1px;
  border-top-color: transparent;
  border-radius: 0 0 8px 8px;
  padding-bottom: 8px;
}

[data-position="above"] .column-autocomplete-result-list {
  margin-bottom: -1px;
  border-bottom-color: transparent;
  border-radius: 8px 8px 0 0;
  padding-top: 8px;
}

/* Single result item */
.column-autocomplete-result {
  cursor: default;
  padding: 12px 12px 12px 12px;
  background-repeat: no-repeat;
  background-position: 12px center;
  color: black;
  
}

.column-autocomplete-result:hover,
.column-autocomplete-result[aria-selected="true"] {
  background-color: rgba(0, 0, 0, 0.06);
}
</style>